export type EventBookingPackage = {
    id: string
    price: number
    originalPrice?: number | null
    earlyBirdDeadline?: string | null
}

export type EventBookingInput = {
    eventId: string
    packages: EventBookingPackage[]
    selectedPackageId: string
    startDate: Date | string | null | undefined
    minPrice: number | null | undefined
    minOriginalPrice?: number | null
    earlyBirdDeadline?: string | null
    now: Date
    promptRequested: boolean
    fullyBooked?: boolean
}

export type EventBookingState = {
    isPast: boolean
    isFull: boolean
    canBook: boolean
    hasMultiplePackages: boolean
    needsPackageSelection: boolean
    showPackagePrompt: boolean
    displayedPrice: number | null
    displayedOriginalPrice: number | null
    displayedEarlyBirdDeadline: string | null
    showFromSuffix: boolean
    checkoutHref: string
}

export const deriveEventBookingState = ({
    eventId,
    packages,
    selectedPackageId,
    startDate,
    minPrice,
    minOriginalPrice,
    earlyBirdDeadline,
    now,
    promptRequested,
    fullyBooked = false,
}: EventBookingInput): EventBookingState => {
    const hasPackages = packages.length > 0
    const hasMultiplePackages = packages.length > 1
    const selectedPackage = packages.find((p) => p.id === selectedPackageId)
    const resolvedPackageId = hasMultiplePackages
        ? selectedPackageId || null
        : hasPackages
          ? packages[0].id
          : null
    const needsPackageSelection = hasMultiplePackages && !selectedPackageId

    const isPast = startDate ? new Date(startDate) < now : false

    return {
        isPast,
        isFull: fullyBooked,
        // A full event is not bookable through any path, so the page stops offering it rather than
        // sending someone to a checkout that will refuse them.
        canBook: !isPast && !fullyBooked,
        hasMultiplePackages,
        needsPackageSelection,
        showPackagePrompt: promptRequested && needsPackageSelection,
        displayedPrice: selectedPackage?.price ?? minPrice ?? null,
        displayedOriginalPrice: selectedPackage
            ? (selectedPackage.originalPrice ?? null)
            : (minOriginalPrice ?? null),
        displayedEarlyBirdDeadline: selectedPackage
            ? (selectedPackage.earlyBirdDeadline ?? null)
            : (earlyBirdDeadline ?? null),
        showFromSuffix: hasMultiplePackages && !selectedPackage,
        checkoutHref: resolvedPackageId
            ? `/checkout?eventId=${eventId}&eventPackageId=${resolvedPackageId}`
            : `/checkout?eventId=${eventId}`,
    }
}
